import React, { useRef } from 'react';
import { GlossaryId } from '../../content/glossary';
import InfoTip from '../InfoTip';
import * as fmt from '../../lib/format';

export interface FlowTab {
  id: string;
  label: string;
  count?: number;
  /** What the tab is, in plain words; shown as a small (i) at the end of the strip while the tab is open. */
  tip?: GlossaryId;
}

interface Props {
  tabs: FlowTab[];
  active: string;
  onChange: (id: string) => void;
  /** Names the tab list for screen readers. */
  label?: string;
  /** Prefix of the ids that tie each tab to its panel: `${idPrefix}-tab-x` and `${idPrefix}-panel-x`. */
  idPrefix?: string;
}

// Underlined tabs with the keyboard behaviour of a tab list: arrows move between tabs.
export default function FlowTabs({ tabs, active, onChange, label = 'Flow views', idPrefix = 'flow' }: Props) {
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});

  const onKey = (e: React.KeyboardEvent, index: number) => {
    const step = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
    if (!step) return;
    e.preventDefault();
    const next = tabs[(index + step + tabs.length) % tabs.length];
    onChange(next.id);
    refs.current[next.id]?.focus();
  };

  const activeTip = tabs.find((t) => t.id === active)?.tip;

  return (
    <div className="mb-5 flex items-end border-b border-gray-200">
      {/* The strip scrolls inside its own box on a narrow screen (a label never wraps over several lines); the 1px overlap with the line below sits on the strip, not on the tabs, so scrolling does not clip the underline. */}
      <div role="tablist" aria-label={label} className="-mb-px flex min-w-0 gap-1 overflow-x-auto">
        {tabs.map((t, i) => {
          const on = t.id === active;
          return (
            <button
              key={t.id}
              ref={(el) => { refs.current[t.id] = el; }}
              type="button"
              role="tab"
              id={`${idPrefix}-tab-${t.id}`}
              aria-selected={on}
              aria-controls={`${idPrefix}-panel-${t.id}`}
              tabIndex={on ? 0 : -1}
              onClick={() => onChange(t.id)}
              onKeyDown={(e) => onKey(e, i)}
              className={`inline-flex shrink-0 items-center gap-2 whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-medium transition-colors duration-150 motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500 ${
                on ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              {t.label}
              {t.count !== undefined && (
                <span className={`rounded-full px-1.5 py-0.5 text-[0.6875rem] font-semibold tabular-nums ${on ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                  {fmt.count(t.count)}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {activeTip && <span className="ml-auto shrink-0 self-center pl-2 pr-2"><InfoTip id={activeTip} className="" /></span>}
    </div>
  );
}
